import { motion } from 'framer-motion'
import {
    AlertTriangle,
    CheckCircle,
    Clock,
    Eye,
    EyeOff,
    Plus,
    Search,
    Trash2,
    Users,
    X
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Input } from '../ui/input'

const UserManagement = ({ currentUser }) => {
    const [users, setUsers] = useState([])
    const [filteredUsers, setFilteredUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedRole, setSelectedRole] = useState('all')
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [newUser, setNewUser] = useState({
        username: '',
        email: '',
        password: '',
        full_name: '',
        role: 'user',
        department: '',
        phone: '',
        license_number: ''
    })
    const [message, setMessage] = useState({ type: '', text: '' })
    const [currentPage, setCurrentPage] = useState(1)
    const [usersPerPage] = useState(10)
    const [sortField, setSortField] = useState('created_at')
    const [sortDirection, setSortDirection] = useState('desc')
    const [showPassword, setShowPassword] = useState({})
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [userToDelete, setUserToDelete] = useState(null)
    const [adminPassword, setAdminPassword] = useState('')
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [validationErrors, setValidationErrors] = useState({})

    // 檢查是否可以刪除特定用戶
    const canDeleteUser = (targetUser) => {
        if (!currentUser || !targetUser) return false

        // 不能刪除自己
        if (targetUser.id === currentUser.id) return false

        const currentRole = currentUser.role
        const targetRole = targetUser.role

        // 開發人員可以刪除除自己以外的任何用戶
        if (currentRole === 'developer') {
            return true
        }

        // 超級管理員可以刪除管理員和普通用戶，但不能刪除開發人員
        if (currentRole === 'super_admin') {
            return targetRole !== 'developer'
        }

        // 一般管理員不能刪除其他管理員、超級管理員或開發人員
        if (currentRole === 'admin') {
            return targetRole !== 'admin' && targetRole !== 'super_admin' && targetRole !== 'developer'
        }

        return false
    }

    // 檢查是否可以創建特定角色的用戶
    const canCreateUserWithRole = (targetRole) => {
        if (!currentUser) return false

        const currentRole = currentUser.role

        // 開發人員可以創建任何角色
        if (currentRole === 'developer') {
            return true
        }

        // 超級管理員可以創建管理員和普通用戶，但不能創建開發人員
        if (currentRole === 'super_admin') {
            return targetRole !== 'developer'
        }

        // 一般管理員只能創建普通用戶
        if (currentRole === 'admin') {
            return ['user', 'attorney', 'manager'].includes(targetRole)
        }

        return false
    }

    // 即時驗證函數
    const validateField = (fieldName, value) => {
        const errors = { ...validationErrors }

        switch (fieldName) {
            case 'username':
                if (!value.trim()) {
                    errors.username = '用戶名不能為空'
                } else if (value.length < 3) {
                    errors.username = '用戶名至少需要3個字符'
                } else if (!/^[a-zA-Z0-9_]+$/.test(value)) {
                    errors.username = '用戶名只能包含字母、數字和下劃線'
                } else {
                    delete errors.username
                }
                break
            case 'password':
                if (!value) {
                    errors.password = '密碼不能為空'
                } else if (value.length < 6) {
                    errors.password = '密碼至少需要6個字符'
                } else {
                    delete errors.password
                }
                break
            case 'email':
                if (!value.trim()) {
                    errors.email = '郵箱不能為空'
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    errors.email = '郵箱格式不正確'
                } else {
                    delete errors.email
                }
                break
            case 'full_name':
                if (!value.trim()) {
                    errors.full_name = '姓名不能為空'
                } else {
                    delete errors.full_name
                }
                break
            case 'role':
                if (!canCreateUserWithRole(value)) {
                    errors.role = `您沒有權限創建 ${value} 角色的用戶`
                } else {
                    delete errors.role
                }
                break
            default:
                break
        }

        setValidationErrors(errors)
        return Object.keys(errors).length === 0
    }

    // 處理輸入變更並驗證
    const handleInputChange = (fieldName, value) => {
        setNewUser(prev => ({ ...prev, [fieldName]: value }))
        validateField(fieldName, value)

        // 如果有全局錯誤訊息，清除它
        if (message.text) {
            setMessage({ type: '', text: '' })
        }
    }

    useEffect(() => {
        fetchUsers()
    }, [])

    useEffect(() => {
        filterUsers()
    }, [users, searchTerm, selectedRole])

    // 確保新用戶的默認角色是當前用戶可以創建的
    useEffect(() => {
        if (!canCreateUserWithRole(newUser.role)) {
            // 如果當前選擇的角色不被允許，則重置為 'user'
            setNewUser(prev => ({ ...prev, role: 'user' }))
        }
    }, [currentUser, newUser.role])

    const fetchUsers = async () => {
        try {
            const response = await fetch('/api/user/users', {
                credentials: 'include'
            })
            if (response.ok) {
                const data = await response.json()
                // 生產環境不輸出敏感資訊
                setUsers(data.data || []) // 修正：使用 data.data 而不是 data.users
            } else {
                console.error('獲取用戶列表失敗:', response.status, response.statusText)
                setMessage({ type: 'error', text: '獲取用戶列表失敗: {{status}}' })
            }
        } catch (error) {
            console.error('Error fetching users:', error)
            setMessage({ type: 'error', text: '網絡錯誤，請稍後再試' })
        } finally {
            setLoading(false)
        }
    }

    const createUser = async (e) => {
        e.preventDefault()

        // 檢查是否有權限創建此角色的用戶
        if (!canCreateUserWithRole(newUser.role)) {
            setMessage({
                type: 'error',
                text: `您沒有權限創建 ${newUser.role} 角色的用戶`
            })
            return
        }

        // 驗證所有必填欄位
        const fieldsToValidate = ['username', 'email', 'password', 'full_name', 'role']
        let hasErrors = false

        fieldsToValidate.forEach(field => {
            const value = newUser[field]
            const isValid = validateField(field, value)
            if (!isValid) hasErrors = true
        })

        if (hasErrors) {
            setMessage({
                type: 'error',
                text: '請修正表單中的錯誤後再提交'
            })
            return
        }

        setLoading(true)
        try {
            const response = await fetch('/api/user/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(newUser)
            })

            const data = await response.json()
            // 移除敏感資訊輸出

            if (response.ok) {
                setMessage({ type: 'success', text: '用戶創建成功' })
                setShowCreateForm(false)
                setNewUser({
                    username: '',
                    email: '',
                    password: '',
                    full_name: '',
                    role: 'user',
                    department: '',
                    phone: '',
                    license_number: ''
                })
                fetchUsers()
            } else if (response.status === 401) {
                setMessage({ type: 'error', text: '登入狀態已過期，請重新登入' })
            } else if (response.status === 403) {
                setMessage({ type: 'error', text: '權限不足，需要管理員權限才能創建用戶' })
            } else {
                setMessage({ type: 'error', text: data.message || '創建用戶失敗' })
            }
        } catch (error) {
            console.error('Error creating user:', error)
            setMessage({ type: 'error', text: '網絡錯誤，請稍後再試' })
        } finally {
            setLoading(false)
        }
    }

    const deleteUser = async (userId) => {
        const user = users.find(u => u.id === userId)
        if (!user) return

        // 檢查是否有權限刪除此用戶
        if (!canDeleteUser(user)) {
            setMessage({
                type: 'error',
                text: '您沒有權限刪除此用戶'
            })
            return
        }

        setUserToDelete(user)
        setShowDeleteConfirm(true)
    }

    const confirmDeleteUser = async () => {
        if (!userToDelete || !adminPassword.trim()) {
            setMessage({ type: 'error', text: '請輸入管理員密碼' })
            return
        }

        setDeleteLoading(true)
        try {
            // 先驗證管理員密碼
            const verifyResponse = await fetch('/api/user/verify-admin-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ password: adminPassword })
            })

            if (!verifyResponse.ok) {
                const verifyData = await verifyResponse.json()
                setMessage({ type: 'error', text: verifyData.message || '管理員密碼驗證失敗' })
                setDeleteLoading(false)
                return
            }

            // 密碼驗證成功，執行刪除
            const response = await fetch(`/api/user/users/${userToDelete.id}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (response.ok) {
                setMessage({
                    type: 'success', text: '用戶 {{username}} ({{fullName}}) 刪除成功'
                })
                fetchUsers()
                setShowDeleteConfirm(false)
                setUserToDelete(null)
                setAdminPassword('')
            } else {
                const data = await response.json()
                setMessage({ type: 'error', text: data.error || '刪除用戶失敗' })
            }
        } catch (error) {
            console.error('Error deleting user:', error)
            setMessage({ type: 'error', text: '網絡錯誤，請稍後再試' })
        } finally {
            setDeleteLoading(false)
        }
    }

    // 更新用戶權限
    const updateUserPermission = async (userId, permissionField, value) => {
        try {
            const response = await fetch(`/api/user/users/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    [permissionField]: value
                })
            })

            const data = await response.json()

            if (response.ok) {
                setMessage({ type: 'success', text: `權限更新成功` })
                fetchUsers() // 重新載入用戶列表
            } else {
                setMessage({ type: 'error', text: data.message || '權限更新失敗' })
            }
        } catch (error) {
            console.error('Error updating permission:', error)
            setMessage({ type: 'error', text: '網絡錯誤，請稍後再試' })
        }
    }

    const cancelDelete = () => {
        setShowDeleteConfirm(false)
        setUserToDelete(null)
        setAdminPassword('')
        setDeleteLoading(false)
    }

    const filterUsers = () => {
        let filtered = users.filter(user => {
            const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
            const matchesRole = selectedRole === 'all' || user.role === selectedRole
            return matchesSearch && matchesRole
        })

        // 排序
        filtered.sort((a, b) => {
            const aVal = a[sortField] || ''
            const bVal = b[sortField] || ''

            if (sortDirection === 'asc') {
                return aVal.toString().localeCompare(bVal.toString())
            } else {
                return bVal.toString().localeCompare(aVal.toString())
            }
        })

        setFilteredUsers(filtered)
        setCurrentPage(1)
    }

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
            case 'developer': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
        }
    }

    const getRoleDisplayName = (role) => {
        switch (role) {
            case 'admin': return '管理員'
            case 'developer': return '開發人員'
            default: return '用戶'
        }
    }

    const togglePasswordVisibility = (userId) => {
        setShowPassword(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }))
    }

    // 分頁計算
    const indexOfLastUser = currentPage * usersPerPage
    const indexOfFirstUser = indexOfLastUser - usersPerPage
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser)
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage)

    const paginate = (pageNumber) => setCurrentPage(pageNumber)

    if (loading && users.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2">{'載入中...'}</span>
            </div>
        )
    }

    return (
        <div className="space-y-6 pb-20">
            {/* 消息提示 - 置頂顯示 */}
            {message.text && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`p-4 rounded-lg border-l-4 ${message.type === 'success'
                        ? 'bg-green-100 text-green-800 border-green-500 dark:bg-green-900/20 dark:text-green-400 dark:border-green-600'
                        : 'bg-red-100 text-red-800 border-red-500 dark:bg-red-900/20 dark:text-red-400 dark:border-red-600'
                        }`}
                    style={{ position: 'sticky', top: '0', zIndex: 50 }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <AlertTriangle className="h-5 w-5 mr-2" />
                            <span className="font-medium">{message.text}</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMessage({ type: '', text: '' })}
                            className="text-current hover:bg-black/10 dark:hover:bg-white/10"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </motion.div>
            )}

            {/* 標題區域 */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{'用戶管理'}</h1>
                </div>
                <Button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    {'新增用戶'}
                </Button>
            </div>

            {/* 搜索和篩選 */}
            <Card className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder={'搜尋用戶名、姓名或郵箱...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                            <option value="all">{'所有角色'}</option>
                            <option value="admin">{'管理員'}</option>
                            <option value="developer">{'開發人員'}</option>
                            <option value="user">{'用戶'}</option>
                        </select>
                    </div>
                </div>
            </Card>

            {/* 用戶列表 */}
            <Card>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {'用戶信息'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {'角色'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {'啟用'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {'管理員權限'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {'查看原始結果'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {'創建時間'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {'操作'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {currentUsers.map((user) => (
                                <motion.tr
                                    key={user.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                                                    <span className="text-sm font-medium text-white">
                                                        {user.username.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {user.full_name || user.username}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {user.email}
                                                </div>
                                                <div className="text-xs text-gray-400 dark:text-gray-500">
                                                    @{user.username}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <Badge className={getRoleColor(user.role)}>
                                            {getRoleDisplayName(user.role)}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                                            <span className="text-sm text-gray-900 dark:text-white">{'活躍'}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="space-y-1">
                                            <label className="flex items-center text-xs">
                                                <input
                                                    type="checkbox"
                                                    checked={user.can_access_health_check || user.role === 'super_admin' || user.role === 'admin' || user.role === 'developer'}
                                                    disabled={user.role === 'super_admin' || user.role === 'admin' || user.role === 'developer'}
                                                    onChange={(e) => updateUserPermission(user.id, 'can_access_health_check', e.target.checked)}
                                                    className="mr-2"
                                                />
                                                <span className="text-gray-700 dark:text-gray-300">API健康檢查</span>
                                            </label>
                                            <label className="flex items-center text-xs">
                                                <input
                                                    type="checkbox"
                                                    checked={user.can_access_dashboard || user.role === 'super_admin' || user.role === 'admin' || user.role === 'developer'}
                                                    disabled={user.role === 'super_admin' || user.role === 'admin' || user.role === 'developer'}
                                                    onChange={(e) => updateUserPermission(user.id, 'can_access_dashboard', e.target.checked)}
                                                    className="mr-2"
                                                />
                                                <span className="text-gray-700 dark:text-gray-300">儀表板</span>
                                            </label>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <label className="flex items-center text-xs">
                                            <input
                                                type="checkbox"
                                                checked={user.can_view_document_analysis_raw || user.role === 'super_admin' || user.role === 'admin' || user.role === 'developer'}
                                                disabled={user.role === 'super_admin' || user.role === 'admin' || user.role === 'developer'}
                                                onChange={(e) => updateUserPermission(user.id, 'can_view_document_analysis_raw', e.target.checked)}
                                                className="mr-2"
                                            />
                                            <span className="text-gray-700 dark:text-gray-300">查看文件分析原始結果</span>
                                        </label>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        <div className="flex items-center">
                                            <Clock className="h-4 w-4 mr-2" />
                                            {user.created_at ? new Date(user.created_at).toLocaleDateString('zh-TW') : 'N/A'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => deleteUser(user.id)}
                                                disabled={!canDeleteUser(user)}
                                                className={canDeleteUser(user)
                                                    ? 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                    : 'text-gray-400 cursor-not-allowed'
                                                }
                                                title={!canDeleteUser(user) ? '無權限刪除此用戶' : '刪除用戶'}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 分頁 */}
                {totalPages > 1 && (
                    <div className="bg-white dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 mb-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <Button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                variant="outline"
                            >
                                {'上一頁'}
                            </Button>
                            <Button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                variant="outline"
                            >
                                {'下一頁'}
                            </Button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    {'顯示第 {{from}} 到 {{to}} 項，共 {{total}} 項結果'}
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <Button
                                            key={i + 1}
                                            onClick={() => paginate(i + 1)}
                                            variant={currentPage === i + 1 ? "default" : "outline"}
                                            size="sm"
                                            className="mx-1"
                                        >
                                            {i + 1}
                                        </Button>
                                    ))}
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* 創建用戶表單 */}
            {showCreateForm && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowCreateForm(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{'創建新用戶'}</h2>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowCreateForm(false)}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* 表單內警告訊息 */}
                        {message.text && showCreateForm && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className={`mb-4 p-3 rounded-lg border-l-4 ${message.type === 'success'
                                    ? 'bg-green-50 text-green-800 border-green-400 dark:bg-green-900/20 dark:text-green-400 dark:border-green-600'
                                    : 'bg-red-50 text-red-800 border-red-400 dark:bg-red-900/20 dark:text-red-400 dark:border-red-600'
                                    }`}
                            >
                                <div className="flex items-start">
                                    <AlertTriangle className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" />
                                    <span className="text-sm font-medium">{message.text}</span>
                                </div>
                            </motion.div>
                        )}

                        <form onSubmit={createUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'用戶名'} *
                                </label>
                                <Input
                                    type="text"
                                    required
                                    value={newUser.username}
                                    onChange={(e) => handleInputChange('username', e.target.value)}
                                    placeholder={'輸入用戶名'}
                                    className={validationErrors.username ? 'border-red-500' : ''}
                                />
                                {validationErrors.username && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors.username}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'郵箱'} *
                                </label>
                                <Input
                                    type="email"
                                    required
                                    value={newUser.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    placeholder={'輸入郵箱地址'}
                                    className={validationErrors.email ? 'border-red-500' : ''}
                                />
                                {validationErrors.email && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'密碼'} *
                                </label>
                                <div className="relative">
                                    <Input
                                        type={showPassword.new ? "text" : "password"}
                                        required
                                        value={newUser.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        placeholder={'輸入密碼'}
                                        className={`pr-10 ${validationErrors.password ? 'border-red-500' : ''}`}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3"
                                        onClick={() => togglePasswordVisibility('new')}
                                    >
                                        {showPassword.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                                {validationErrors.password && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'姓名'}
                                </label>
                                <Input
                                    type="text"
                                    value={newUser.full_name}
                                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                                    placeholder={'輸入真實姓名'}
                                    className={validationErrors.full_name ? 'border-red-500' : ''}
                                />
                                {validationErrors.full_name && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors.full_name}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'角色'} *
                                </label>
                                <select
                                    required
                                    value={newUser.role}
                                    onChange={(e) => handleInputChange('role', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${validationErrors.role ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                >
                                    <option value="user">{'用戶'}</option>
                                    {canCreateUserWithRole('admin') && (
                                        <option value="admin">{'管理員'}</option>
                                    )}
                                    {canCreateUserWithRole('developer') && (
                                        <option value="developer">{'開發人員'}</option>
                                    )}
                                </select>
                                {validationErrors.role && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors.role}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'部門'}
                                </label>
                                <Input
                                    type="text"
                                    value={newUser.department}
                                    onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                                    placeholder={'輸入部門'}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'電話'}
                                </label>
                                <Input
                                    type="text"
                                    value={newUser.phone}
                                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                                    placeholder={'輸入電話號碼'}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {'執照號碼'}
                                </label>
                                <Input
                                    type="text"
                                    value={newUser.license_number}
                                    onChange={(e) => setNewUser({ ...newUser, license_number: e.target.value })}
                                    placeholder={'輸入執照號碼'}
                                />
                            </div>

                            <div className="flex justify-end space-x-2 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowCreateForm(false)}
                                >
                                    {'取消'}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    {loading ? '創建中...' : '創建用戶'}
                                </Button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}

            {/* 刪除確認對話框 */}
            {showDeleteConfirm && userToDelete && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={cancelDelete}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{'確認刪除用戶'}</h2>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={cancelDelete}
                                disabled={deleteLoading}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="mb-6">
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                                <p className="text-red-800 dark:text-red-200 font-medium">
                                    {'您即將刪除以下用戶：'}
                                </p>
                                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                                    <p><strong>ID:</strong> {userToDelete.id}</p>
                                    <p><strong>{'用戶名'}:</strong> {userToDelete.username}</p>
                                    <p><strong>{'姓名'}:</strong> {userToDelete.full_name || '未設定'}</p>
                                    <p><strong>{'郵箱'}:</strong> {userToDelete.email}</p>
                                    <p><strong>{'角色'}:</strong> {userToDelete.role_name || userToDelete.role}</p>
                                </div>
                            </div>

                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                                <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                                    <strong>{'警告'}：</strong>{'此操作無法撤銷！用戶的所有相關數據將被永久刪除。'}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {'請輸入您的管理員密碼以確認刪除：'}
                                </label>
                                <div className="relative">
                                    <Input
                                        type={showPassword.delete ? "text" : "password"}
                                        value={adminPassword}
                                        onChange={(e) => setAdminPassword(e.target.value)}
                                        placeholder={'輸入管理員密碼'}
                                        className="pr-10"
                                        disabled={deleteLoading}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !deleteLoading) {
                                                confirmDeleteUser()
                                            }
                                        }}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3"
                                        onClick={() => setShowPassword(prev => ({ ...prev, delete: !prev.delete }))}
                                        disabled={deleteLoading}
                                    >
                                        {showPassword.delete ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={cancelDelete}
                                disabled={deleteLoading}
                            >
                                {'取消'}
                            </Button>
                            <Button
                                type="button"
                                onClick={confirmDeleteUser}
                                disabled={deleteLoading || !adminPassword.trim()}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deleteLoading ? '刪除中...' : '確認刪除'}
                            </Button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    )
}

export default UserManagement
